import { useEffect, useRef, useState } from "react";
import { RefreshCcw, Check } from "lucide-react";
import { toast } from "sonner";
import { QUIZ_QUESTIONS } from "@/lib/quizData";
import { shuffle } from "@/lib/gameData";
import { GAME_FEEDBACK_MS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const QUIZ_LENGTH = 5;

function buildQuiz() {
  return shuffle(QUIZ_QUESTIONS).slice(0, QUIZ_LENGTH);
}

export default function PhonicsQuiz() {
  const [quiz, setQuiz] = useState(buildQuiz);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [done, setDone] = useState(false);
  const timersRef = useRef([]);
  const { award } = useAuth();

  const clearTimers = () => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  };
  useEffect(() => () => clearTimers(), []);

  const current = quiz[idx];

  const restart = () => {
    clearTimers();
    setQuiz(buildQuiz());
    setIdx(0);
    setPicked(null);
    setCorrectCount(0);
    setDone(false);
  };

  const pick = (option) => {
    if (picked !== null) return;
    setPicked(option);
    const isCorrect = option === current.correct;
    if (isCorrect) {
      setCorrectCount((c) => c + 1);
      award(10, "quiz");
      toast.success("Correct!");
    } else {
      toast.error(`Answer: ${current.correct}`);
    }
  };

  const nextQuestion = () => {
    if (idx + 1 >= quiz.length) {
      setDone(true);
      return;
    }
    setPicked(null);
    setIdx((i) => i + 1);
  };

  if (done) {
    const total = quiz.length;
    const pct = Math.round((correctCount / total) * 100);
    const bandLabel =
      pct >= 90 ? "Phonics master" : pct >= 70 ? "Strong reader" : pct >= 50 ? "Getting there" : "Keep practising";
    return (
      <div className="text-center py-6">
        <div className="inline-flex items-center gap-2 bg-fuchsia-50 text-fuchsia-700 px-4 py-2 rounded-full font-bold text-sm mb-6">
          Quiz complete
        </div>
        <div
          data-testid="quiz-final-score"
          className="font-display text-6xl sm:text-7xl font-semibold text-fuchsia-600 leading-none"
        >
          {correctCount} / {total}
        </div>
        <p className="mt-3 text-slate-600 text-lg">
          {pct}% &mdash; <span className="font-bold text-fuchsia-600">{bandLabel}</span>
        </p>
        <button
          data-testid="quiz-restart"
          onClick={restart}
          className="mt-8 inline-flex items-center gap-2 rounded-full px-8 py-4 bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-bold shadow-md hover:shadow-lg transition-all"
        >
          <RefreshCcw className="w-4 h-4" /> Try again
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="inline-flex items-center gap-2 bg-fuchsia-50 text-fuchsia-700 px-4 py-2 rounded-full font-bold text-sm">
          Adult phonics quiz
        </div>
        <div
          data-testid="quiz-progress"
          className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-4 py-2 rounded-full font-bold text-sm"
        >
          Question {idx + 1} / {quiz.length}
        </div>
      </div>

      <div className="bg-slate-50 rounded-3xl p-6 sm:p-8 mb-6">
        <p className="text-sm font-bold uppercase tracking-widest text-slate-500 mb-3">
          Age 16+ · Reading & phonics
        </p>
        <h3
          data-testid="quiz-question"
          className="font-display text-xl sm:text-2xl font-medium text-slate-900 leading-snug"
        >
          {current.question}
        </h3>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        {current.options.map((opt, i) => {
          const isCorrect = picked !== null && opt === current.correct;
          const isWrongPick = picked === opt && opt !== current.correct;
          return (
            <button
              key={`${current.id}-opt-${i}`}
              data-testid={`quiz-option-${i}`}
              onClick={() => pick(opt)}
              disabled={picked !== null}
              className={`text-left rounded-2xl border-2 p-4 font-bold text-lg transition-all
                ${isCorrect ? "bg-emerald-500 border-emerald-600 text-white phonx-letter-pop" : ""}
                ${isWrongPick ? "bg-rose-100 border-rose-300 text-rose-700 phonx-shake" : ""}
                ${!picked ? "bg-white border-slate-200 text-slate-800 hover:border-fuchsia-400 hover:text-fuchsia-600 shadow-sm hover:shadow-md" : ""}
                ${picked && !isCorrect && !isWrongPick ? "bg-white border-slate-200 text-slate-500" : ""}
              `}
            >
              {opt}
            </button>
          );
        })}
      </div>

      {picked !== null && (
        <div className="bg-fuchsia-50 border-2 border-fuchsia-100 rounded-2xl p-4 mb-4 text-slate-700 leading-relaxed">
          <span className="font-bold text-fuchsia-700">Why: </span>
          {current.note}
        </div>
      )}

      <div className="flex justify-center">
        <button
          data-testid="quiz-next"
          onClick={nextQuestion}
          disabled={picked === null}
          className="rounded-full px-8 py-3 bg-fuchsia-500 hover:bg-fuchsia-600 disabled:bg-fuchsia-200 text-white font-bold shadow-md hover:shadow-lg transition-all inline-flex items-center gap-2"
        >
          <Check className="w-5 h-5" />
          {idx + 1 >= quiz.length ? "See results" : "Next question"}
        </button>
      </div>
    </div>
  );
}
